import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Search, ArrowRight, Clock, CalendarDays } from "lucide-react";
import { BLOG_POSTS, BLOG_TAGS } from "@/data/blogPosts";
import { Section, Overline, Reveal } from "@/components/Primitives";
import { Seo } from "@/components/Seo";

const fmtDate = (d) => new Date(d).toLocaleDateString("fr-FR", { day: "numeric", month: "long", year: "numeric" });

export default function Blog() {
  const [query, setQuery] = useState("");
  const [tag, setTag] = useState("Tous");

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return BLOG_POSTS
      .filter((p) => (tag === "Tous" ? true : p.tags.includes(tag)))
      .filter((p) => (!q ? true : (p.title + " " + p.excerpt + " " + p.intro).toLowerCase().includes(q)))
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  }, [query, tag]);

  const pills = ["Tous", ...BLOG_TAGS];

  const schema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    name: "Blog Work & Travel Expert",
    description: "Guides et conseils sur l'immigration et les visas vers l'Australie et le Canada.",
    url: "https://worktravelexpert.net/blog",
    inLanguage: "fr-FR",
    blogPost: BLOG_POSTS.map((p) => ({
      "@type": "BlogPosting",
      headline: p.title,
      datePublished: p.date,
      url: `https://worktravelexpert.net/blog/${p.slug}`,
    })),
  };

  return (
    <div data-testid="blog-page">
      <Seo
        title="Blog & Guides — Immigration Australie & Canada | Work & Travel Expert"
        description="Guides pratiques et conseils d'experts pour votre projet d'immigration : PVT, visas, emploi, budget et vie sur place en Australie et au Canada."
        keywords="blog immigration, guides visa australie canada, conseils PVT, expatriation australie canada"
        path="/blog"
        schema={schema}
      />

      {/* HERO */}
      <div className="bg-navy">
        <Section className="py-20 sm:py-24">
          <Reveal>
            <Overline>Blog &amp; Guides</Overline>
            <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-medium text-white leading-[1.1]">Conseils & guides pour votre départ</h1>
            <div className="gold-line mt-6" />
            <p className="mt-6 text-white/75 max-w-2xl leading-relaxed">Toute l'expertise Work &amp; Travel Expert pour préparer votre projet en Australie ou au Canada : visas, PVT, emploi, budget et vie sur place.</p>

            {/* SEARCH */}
            <div className="mt-8 relative max-w-md">
              <Search size={18} className="absolute left-4 top-1/2 -translate-y-1/2 text-navy/40" />
              <input
                data-testid="blog-search-input"
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Rechercher un article…"
                className="w-full bg-white text-navy pl-11 pr-4 py-3.5 outline-none focus:ring-2 focus:ring-gold placeholder:text-navy/40"
              />
            </div>
          </Reveal>
        </Section>
      </div>

      {/* CATEGORIES */}
      <Section className="pt-12">
        <div className="flex flex-wrap gap-2.5" data-testid="blog-categories">
          {pills.map((p) => (
            <button
              key={p}
              data-testid={`blog-cat-${p.toLowerCase().replace(/\s+/g, "-")}`}
              onClick={() => setTag(p)}
              className={`px-4 py-2 text-sm font-medium border transition-colors ${
                tag === p ? "bg-gold border-gold text-white" : "bg-white border-border text-navy hover:border-gold"
              }`}
            >
              {p}
            </button>
          ))}
        </div>
      </Section>

      {/* LIST */}
      <Section className="py-14">
        {filtered.length === 0 ? (
          <div data-testid="blog-empty" className="border border-dashed border-gold/50 bg-[#F8F9FA] py-16 text-center text-muted-foreground">
            Aucun article ne correspond à votre recherche.
          </div>
        ) : (
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {filtered.map((p, i) => (
              <Reveal key={p.slug} delay={(i % 3) * 0.08}>
                <Link to={`/blog/${p.slug}`} data-testid={`blog-card-${p.slug}`} className="group flex flex-col h-full bg-white border border-border hover:border-gold transition-colors">
                  <div className="relative h-52 overflow-hidden">
                    <img src={p.image} alt={p.h1} loading="lazy" decoding="async" className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
                    <span className="absolute top-3 left-3 bg-navy/90 text-white text-xs font-semibold px-3 py-1">{p.tags[0]}</span>
                  </div>
                  <div className="flex flex-col flex-1 p-6">
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5"><CalendarDays size={13} /> {fmtDate(p.date)}</span>
                      <span className="inline-flex items-center gap-1.5"><Clock size={13} /> {p.readTime} min</span>
                    </div>
                    <h2 className="font-heading text-xl text-navy mt-3 leading-snug group-hover:text-gold transition-colors">{p.h1}</h2>
                    <p className="text-sm text-muted-foreground mt-3 leading-relaxed flex-1">{p.excerpt}</p>
                    <span className="inline-flex items-center gap-1.5 mt-5 text-sm font-medium text-gold group-hover:gap-2.5 transition-all">
                      Lire l'article <ArrowRight size={15} />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </Section>
    </div>
  );
}
